import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ApiService from '../../../services/api';

interface Project {
  id: number;
  name: string;
  description: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  estimated_hours: number | null;
  budget: number | null;
  client_id: number | null;
  project_manager_id: number | null;
}

interface Client {
  id: number;
  name: string;
  company?: string;
}

interface User {
  id: number;
  name: string;
  email: string;
}

const AdminProjectEditPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [project, setProject] = useState<Project | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [projectManagers, setProjectManagers] = useState<User[]>([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: '',
    start_date: null as Date | null,
    end_date: null as Date | null,
    estimated_hours: '',
    budget: '',
    client_id: '',
    project_manager_id: ''
  });

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load project and assignable users in parallel
      const [projectRes, usersRes] = await Promise.all([
        ApiService.get(`/admin/projects/${projectId}`),
        ApiService.get('/admin/projects/assignable-users')
      ]);

      const projectData = projectRes.data.data || projectRes.data;
      setProject(projectData);

      // Extract budget from budget_info if it exists
      let budgetValue = '';
      if (projectData.budget_info && typeof projectData.budget_info === 'object') {
        budgetValue = projectData.budget_info.amount?.toString() || '';
      }

      setFormData({
        name: projectData.name || '',
        description: projectData.description || '',
        status: projectData.status || '',
        start_date: projectData.actual_start_date ? new Date(projectData.actual_start_date) : null,
        end_date: projectData.target_completion_date ? new Date(projectData.target_completion_date) : null,
        estimated_hours: projectData.estimated_hours?.toString() || '',
        budget: budgetValue,
        client_id: projectData.client_id?.toString() || '',
        project_manager_id: projectData.project_manager_id?.toString() || ''
      });

      const usersData = usersRes.data.data || usersRes.data;
      setClients(usersData.clients || []);
      setProjectManagers(usersData.projectManagers || []);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Prepare payload
      const payload = {
        name: formData.name,
        description: formData.description,
        status: formData.status,
        start_date: formData.start_date ? formData.start_date.toISOString().split('T')[0] : null,
        end_date: formData.end_date ? formData.end_date.toISOString().split('T')[0] : null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        client_id: formData.client_id ? parseInt(formData.client_id) : null,
        project_manager_id: formData.project_manager_id ? parseInt(formData.project_manager_id) : null
      };

      await ApiService.put(`/admin/projects/${projectId}`, payload);
      
      setSuccess(true);
      
      // Redirect back to details page after 1 second
      setTimeout(() => {
        navigate(`/dashboard/admin/projects/${projectId}`);
      }, 1000);
    } catch (err: any) {
      console.error('Error saving project:', err);
      setError(err.response?.data?.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
      draft: 'default',
      submitted: 'info',
      in_review: 'warning',
      approved: 'success',
      in_progress: 'info',
      completed: 'success',
      closed: 'default',
      cancelled: 'error',
      abandoned: 'error'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Project not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/admin/projects')}
          sx={{ mt: 2 }}
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/dashboard/admin"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard/admin');
          }}
          sx={{ cursor: 'pointer' }}
        >
          Admin Dashboard
        </Link>
        <Link
          color="inherit"
          href="/dashboard/admin/projects"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard/admin/projects');
          }}
          sx={{ cursor: 'pointer' }}
        >
          Projects
        </Link>
        <Link
          color="inherit"
          href={`/dashboard/admin/projects/${projectId}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/dashboard/admin/projects/${projectId}`);
          }}
          sx={{ cursor: 'pointer' }}
        >
          {project.name}
        </Link>
        <Typography color="text.primary">Edit</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/dashboard/admin/projects/${projectId}`)}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Edit Project
        </Typography>
        <Chip
          label={project.status.replace('_', ' ').toUpperCase()}
          color={getStatusColor(project.status)}
          size="small"
        />
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Project updated successfully! Redirecting...
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Edit Form */}
      <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '1200px', mx: 'auto' }}>
        <form onSubmit={handleSubmit}>
          {/* @ts-ignore - Grid API works correctly despite TS errors */}
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* Project Name */}
            {/* @ts-ignore */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Project Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={saving}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={saving}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6} lg={4}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={saving}
                  label="Status"
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="submitted">Submitted</MenuItem>
                  <MenuItem value="in_review">In Review</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="abandoned">Abandoned</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Client */}
            <Grid item xs={12} sm={6} lg={4}>
              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleChange}
                  disabled={saving}
                  label="Client"
                >
                  <MenuItem value="">None</MenuItem>
                  {clients.map(client => (
                    <MenuItem key={client.id} value={client.id.toString()}>
                      {client.name}{client.company ? ` (${client.company})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Project Manager */}
            <Grid item xs={12} sm={6} lg={4}>
              <FormControl fullWidth>
                <InputLabel>Project Manager</InputLabel>
                <Select
                  name="project_manager_id"
                  value={formData.project_manager_id}
                  onChange={handleChange}
                  disabled={saving}
                  label="Project Manager"
                >
                  <MenuItem value="">None</MenuItem>
                  {projectManagers.map(pm => (
                    <MenuItem key={pm.id} value={pm.id.toString()}>
                      {pm.name} ({pm.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Estimated Hours */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Estimated Hours"
                name="estimated_hours"
                value={formData.estimated_hours}
                onChange={handleChange}
                disabled={saving}
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>

            {/* Budget */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                disabled={saving}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
              />
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={formData.start_date}
                  onChange={(date) => setFormData(prev => ({ ...prev, start_date: date }))}
                  disabled={saving}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={formData.end_date}
                  onChange={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                  disabled={saving}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Form Actions */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/dashboard/admin/projects/${projectId}`)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default AdminProjectEditPage;
